/**
 * User profile types for the application
 */

/**
 * User role enumeration matching backend UserRole
 */
export enum UserRole {
  SYSTEM_ADMIN = 'SYSTEM_ADMIN',
  MANAGER = 'MANAGER',
  USER = 'USER'
}

/**
 * User role display information
 */
export interface UserRoleInfo {
  value: UserRole;
  label: string;
  description: string;
  color: string;
}

/**
 * User role configuration
 */
export const USER_ROLES: Record<UserRole, UserRoleInfo> = {
  [UserRole.SYSTEM_ADMIN]: {
    value: UserRole.SYSTEM_ADMIN,
    label: 'システム管理者',
    description: 'ユーザー管理・システム設定',
    color: '#d32f2f'  // Red (MUI error.main: 白文字で WCAG AA 約4.5:1 を満たす)
  },
  [UserRole.MANAGER]: {
    value: UserRole.MANAGER,
    label: 'マネージャー',
    description: 'データの作成・編集・削除',
    color: '#ff9800'  // Orange
  },
  [UserRole.USER]: {
    value: UserRole.USER,
    label: '一般ユーザー',
    description: 'データの閲覧のみ',
    color: '#4caf50'  // Green
  }
};

export interface UserProfile {
  user_id: string;
  display_name: string;
  email: string;
  role: UserRole;
  created_at: string;
  updated_at: string;
  last_login_at?: string;
  created_by?: string;
  // Additional Cognito status information for user management UI
  user_status?: string;
  enabled?: boolean;
}

export interface UserProfileUpdate {
  display_name: string;
}

export interface UserProfileResponse extends UserProfile {}

/**
 * User registration request interface
 */
export interface UserRegistrationRequest {
  email: string;
  display_name: string;
  role: UserRole;
  temporary_password?: string;
}

/**
 * User registration response interface
 */
export interface UserRegistrationResponse {
  user_id: string;
  email: string;
  display_name: string;
  role: UserRole;
  temporary_password: string;
  created_at: string;
}


export interface UserContextType {
  profile: UserProfile | null;
  userRole: UserRole | null;
  loading: boolean;
  error: string | null;
  updateProfile: (data: UserProfileUpdate) => Promise<void>;
  refreshProfile: () => Promise<void>;
  hasPermission: (requiredRole: UserRole) => boolean;
  isSystemAdmin: () => boolean;
  isManagerOrAbove: () => boolean;
}

/**
 * Permission check helper functions
 */
export const hasPermission = (userRole: UserRole | null | undefined, requiredRole: UserRole): boolean => {
  if (!userRole) return false;
  
  const roleHierarchy = {
    [UserRole.SYSTEM_ADMIN]: 3,
    [UserRole.MANAGER]: 2,
    [UserRole.USER]: 1,
  };
  
  const userLevel = roleHierarchy[userRole] || 0;
  const requiredLevel = roleHierarchy[requiredRole] || 0;
  
  return userLevel >= requiredLevel;
};

/**
 * Check if user is system admin
 */
export const isSystemAdmin = (userRole: UserRole | null | undefined): boolean => {
  return userRole === UserRole.SYSTEM_ADMIN;
};

/**
 * Check if user is manager or above
 */
export const isManagerOrAbove = (userRole: UserRole | null | undefined): boolean => {
  return hasPermission(userRole, UserRole.MANAGER);
};